import React, { useMemo, useState } from 'react';

const getValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
};

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const getName = (entity, fallback = '-') => {
  if (typeof entity === 'string' && entity.trim()) return entity.trim();
  if (entity && typeof entity === 'object') {
    return String(entity.name || entity.teamName || entity.shortName || fallback).trim() || fallback;
  }
  return fallback;
};

const getLogoUrl = (...values) => {
  const found = values.find((value) => {
    if (!value) return false;
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'object') return value.logo || value.logoUrl || value.image || value.crest;
    return false;
  });

  if (!found) return '';
  if (typeof found === 'string') return found;
  return found.logo || found.logoUrl || found.image || found.crest || '';
};

const normalizeText = (value = '') =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ı/g, 'i')
    .replace(/ş/g, 's')
    .replace(/ğ/g, 'g')
    .replace(/ç/g, 'c')
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/[^a-z0-9.,+\-\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const formatPercent = (value, fallback = '—') => {
  const numeric = toNumber(value);
  if (numeric === null || numeric <= 0) return fallback;
  return `%${numeric % 1 === 0 ? numeric.toFixed(0) : numeric.toFixed(1)}`;
};

const formatMetricNumber = (value, fallback = '—') => {
  const numeric = toNumber(value);
  if (numeric === null) return fallback;
  if (Number.isInteger(numeric)) return String(numeric);
  return numeric.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
};

const parseMatchDate = (value) => {
  const raw = getValue(value);
  if (!raw) return null;

  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;

  if (typeof raw === 'number' || /^\d{10,13}$/.test(String(raw).trim())) {
    const numeric = Number(raw);
    const timestamp = numeric < 100000000000 ? numeric * 1000 : numeric;
    const date = new Date(timestamp);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const text = String(raw).trim();

  const trMatch = text.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})(?:\s+(\d{1,2}):(\d{2}))?/);
  if (trMatch) {
    const [, day, month, year, hour = '00', minute = '00'] = trMatch;
    const date = new Date(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${minute}:00+03:00`);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const sqlMatch = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:\s+(\d{1,2}):(\d{2})(?::\d{2})?)?$/);
  if (sqlMatch) {
    const [, year, month, day, hour = '00', minute = '00'] = sqlMatch;
    const date = new Date(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${minute}:00+03:00`);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateTime = (value) => {
  const date = parseMatchDate(value);
  if (!date) return '';
  return new Intl.DateTimeFormat('tr-TR', {
    timeZone: 'Europe/Istanbul',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

const getMatchDateLabel = (card = {}) => {
  const primary = formatDateTime(getMatchDateValue(card));
  if (primary) return primary;

  const fallback = formatDateTime(card?.analysisDate || card?.publishedAt || card?.createdAt);
  if (fallback) return fallback;

  return isLiveStatus(card) ? 'Canlı - saat kaynağı yok' : 'Saat kaynağı yok';
};

const getMatchDateValue = (card = {}) => getValue(
  card?.startTime,
  card?.matchDate,
  card?.matchTime,
  card?.date,
  card?.fixtureDate,
  card?.kickoffTime,
  card?.scheduledAt,
  card?.eventDate,
  card?.fixture?.date,
  card?.fixture?.timestamp,
  card?.match?.date,
  card?.match?.fixture?.date,
  card?.match?.fixture?.timestamp,
  card?.analysisDate,
  card?.publishedAt,
  card?.createdAt
);

const buildScoreText = (home, away) => {
  const homeNumber = toNumber(home);
  const awayNumber = toNumber(away);
  if (homeNumber === null || awayNumber === null) return '';
  return `${homeNumber}-${awayNumber}`;
};

const getFinalScoreValue = (card = {}) => {
  const direct = getValue(
    card?.finalScore,
    card?.actualValue,
    card?.actualScore,
    card?.matchScore,
    card?.resultScore,
    card?.score,
    card?.fullTimeScore,
    card?.fixture?.score?.fulltime,
    card?.fixture?.score?.fullTime,
    card?.match?.score?.fulltime,
    card?.match?.score?.fullTime
  );

  if (direct && typeof direct === 'object') {
    const fullTime = direct.fulltime || direct.fullTime || direct.ft || direct.regular || direct.current || direct.goals || direct;
    const built = buildScoreText(
      fullTime.home ?? fullTime.homeTeam ?? fullTime.homeGoals ?? fullTime.home_score,
      fullTime.away ?? fullTime.awayTeam ?? fullTime.awayGoals ?? fullTime.away_score
    );
    if (built) return built;
  }

  if (direct) return String(direct);

  return buildScoreText(
    card?.homeGoals ?? card?.homeScore ?? card?.actualHomeGoals ?? card?.fixture?.goals?.home ?? card?.match?.goals?.home,
    card?.awayGoals ?? card?.awayScore ?? card?.actualAwayGoals ?? card?.fixture?.goals?.away ?? card?.match?.goals?.away
  );
};

const getStatusMeta = (status = 'pending') => {
  const normalized = String(status || 'pending').trim().toLowerCase();

  if (normalized === 'won') {
    return {
      label: 'Tuttu',
      text: '#7dffb7',
      border: 'rgba(46,204,113,.58)',
      bg: 'linear-gradient(145deg, rgba(8,46,29,.88), rgba(7,10,12,.98))',
      chipBg: 'rgba(46,204,113,.14)'
    };
  }

  if (normalized === 'lost') {
    return {
      label: 'Tutmadı',
      text: '#ff8b9a',
      border: 'rgba(231,76,60,.58)',
      bg: 'linear-gradient(145deg, rgba(65,16,22,.86), rgba(7,10,12,.98))',
      chipBg: 'rgba(231,76,60,.14)'
    };
  }

  if (normalized === 'void') {
    return {
      label: 'Geçersiz',
      text: '#cbd5e1',
      border: 'rgba(148,163,184,.34)',
      bg: 'linear-gradient(145deg, rgba(34,36,40,.86), rgba(7,10,12,.98))',
      chipBg: 'rgba(148,163,184,.12)'
    };
  }

  if (normalized === 'live') {
    return {
      label: 'Canlı',
      text: '#7dffb7',
      border: 'rgba(46,204,113,.52)',
      bg: 'linear-gradient(145deg, rgba(8,46,29,.72), rgba(7,10,12,.98))',
      chipBg: 'rgba(46,204,113,.14)'
    };
  }

  return {
    label: 'Beklemede',
    text: '#f5d65f',
    border: 'rgba(212,175,55,.42)',
    bg: 'linear-gradient(145deg, rgba(37,31,13,.92), rgba(7,10,12,.98))',
    chipBg: 'rgba(212,175,55,.12)'
  };
};

const parseScore = (value = '') => {
  const match = String(value || '').match(/(\d+)\s*[-:]\s*(\d+)/);
  if (!match) return null;
  const home = Number(match[1]);
  const away = Number(match[2]);
  if (!Number.isFinite(home) || !Number.isFinite(away)) return null;
  return { home, away, total: home + away };
};

const extractLineNumber = (selection = '') => {
  const match = String(selection || '').replace(',', '.').match(/(\d+(?:\.\d+)?)/);
  return match ? Number(match[1]) : null;
};

const getStatusShort = (card = {}) => String(
  card?.matchStatusShort ||
  card?.statusShort ||
  card?.fixture?.status?.short ||
  card?.match?.status?.short ||
  card?.status?.short ||
  ''
).trim().toUpperCase();

const isLiveStatus = (card = {}) => {
  const status = getStatusShort(card);
  return Boolean(card?.isLive) || ['1H', 'HT', '2H', 'ET', 'BT', 'P', 'LIVE'].includes(status);
};

const isFinishedStatus = (card = {}) => {
  const status = getStatusShort(card);
  if (['FT', 'AET', 'PEN', 'AP', 'AWD', 'WO'].includes(status)) return true;
  if (Boolean(card?.isFinished || card?.finished || card?.matchFinished)) return true;

  const matchTime = parseMatchDate(getMatchDateValue(card));
  if (!matchTime) return false;
  return (Date.now() - matchTime.getTime()) / 60000 > 130;
};

const getTopStatusLabel = (statusMeta = {}, status = 'pending', finalScore = '') => {
  const normalized = String(status || '').toLowerCase();
  const score = String(finalScore || '').trim();

  if (score && ['won', 'lost', 'void'].includes(normalized)) {
    return `${score} • ${statusMeta.label || ''}`.trim();
  }

  if (score) return `Final: ${score}`;
  return statusMeta.label || 'Beklemede';
};

const resolveFinishedStatusFromScore = (card = {}, marketLabel = '', finalScore = '') => {
  const existingStatus = String(card.resultStatus || card.predictionStatus || '').trim().toLowerCase();
  if (['won', 'lost', 'void'].includes(existingStatus)) return existingStatus;

  const score = parseScore(finalScore);
  if (!score) {
    if (isLiveStatus(card)) return 'live';
    if (isFinishedStatus(card) && existingStatus) return existingStatus;
    return existingStatus || 'pending';
  }

  const selection = normalizeText(getValue(card.selection, card.marketLabel, card.market?.label, card.market?.name, card.market, marketLabel));
  if (!selection || selection.includes('takip listesi') || selection.includes('net market onerisi yok') || selection.includes('net oneri yok')) return 'void';

  if (selection === 'ms 1' || selection.includes('ev sahibi')) return score.home > score.away ? 'won' : 'lost';
  if (selection === 'ms 2' || selection.includes('deplasman')) return score.away > score.home ? 'won' : 'lost';
  if (selection === 'ms x' || selection === 'x' || selection.includes('beraberlik')) return score.home === score.away ? 'won' : 'lost';
  if (selection.includes('kg var')) return score.home > 0 && score.away > 0 ? 'won' : 'lost';
  if (selection.includes('kg yok')) return score.home === 0 || score.away === 0 ? 'won' : 'lost';

  if (selection.includes('ust')) {
    const line = extractLineNumber(selection);
    if (Number.isFinite(line)) return score.total > line ? 'won' : 'lost';
  }

  if (selection.includes('alt')) {
    const line = extractLineNumber(selection);
    if (Number.isFinite(line)) return score.total < line ? 'won' : 'lost';
  }

  return 'void';
};

const cleanRecommendationText = (value = '') => {
  const text = String(value || '').trim();
  const normalized = normalizeText(text);

  if (!text || normalized.includes('takip listesi') || normalized.includes('net market onerisi yok') || normalized.includes('net oneri yok') || normalized.includes('mac analizi')) {
    return 'Net AI önerisi yok';
  }

  return text;
};

const getAiRecommendation = (card = {}) => {
  const candidates = [
    card?.bestPrediction?.selection,
    card?.bestPrediction?.market,
    card?.metrics?.modeling?.prematchDecision?.primaryPick?.label,
    card?.metrics?.modeling?.prematchDecision?.fallbackPick?.label,
    card?.metrics?.modeling?.prematchDecision?.nearestEdgePick?.label,
    card?.recommendedPick,
    card?.pick,
    card?.selection,
    card?.marketLabel,
    card?.market?.label,
    card?.market?.name,
    typeof card?.market === 'string' ? card.market : null
  ];

  const found = candidates.find((value) => typeof value === 'string' && value.trim());
  return cleanRecommendationText(found || '');
};

const getAiConfidence = (card = {}) => {
  const candidates = [
    card?.confidenceScore,
    card?.bestPrediction?.probability,
    card?.metrics?.modeling?.prematchDecision?.confidence,
    card?.metrics?.modeling?.confidenceCalibration?.score,
    card?.aiConfidence,
    card?.confidence,
    card?.modelProbability,
    card?.model,
    card?.dataQuality
  ];

  const value = candidates.find((item) => Number.isFinite(Number(item)) && Number(item) > 0);
  return value ? Number(value) : 0;
};

const getScorePrediction = (card = {}, selection = '') => {
  const score = card?.scorePrediction || card?.metrics?.scorePrediction || {};
  const rawFt = getValue(score?.topFullTime?.score, score?.fullTime, score?.fullTimeScore, score?.mostLikelyFullTimeScores?.[0]?.score, card?.scorePredictionFullTime);
  const rawHt = getValue(score?.topHalfTime?.score, score?.halfTime, score?.halfTimeScore, score?.mostLikelyHalfTimeScores?.[0]?.score, card?.scorePredictionHalfTime);

  const ft = reconcileFullTimeScore(card, selection, [rawFt, ...(score?.mostLikelyFullTimeScores || [])]);
  const ht = rawHt ? String(rawHt) : fallbackHalfTimeScoreForFullTime(ft);
  return { ht, ft };
};

const fallbackFullTimeScoreForSelection = (selection = '', card = {}) => {
  const text = normalizeText(selection);
  const xgTotal = toNumber(card?.expectedGoalsTotal || card?.metrics?.expectedGoals?.total || card?.totalExpectedGoals);

  if (text.includes('kg yok')) return '1-0';
  if (text.includes('kg var')) return '1-1';
  if (text.includes('ms 1')) return xgTotal && xgTotal >= 2.5 ? '2-1' : '1-0';
  if (text.includes('ms 2')) return xgTotal && xgTotal >= 2.5 ? '1-2' : '0-1';
  if (text.includes('ms x')) return xgTotal && xgTotal >= 2.5 ? '2-2' : '1-1';
  if (text.includes('ust')) return '2-1';
  if (text.includes('alt')) return '1-0';
  return '-';
};

const isScoreCompatibleWithSelection = (scoreText = '', selection = '') => {
  const score = parseScore(scoreText);
  if (!score) return false;
  const text = normalizeText(selection);

  if (text.includes('kg var')) return score.home > 0 && score.away > 0;
  if (text.includes('kg yok')) return score.home === 0 || score.away === 0;
  if (text.includes('ms 1')) return score.home > score.away;
  if (text.includes('ms 2')) return score.away > score.home;
  if (text.includes('ms x')) return score.home === score.away;

  if (text.includes('ust')) {
    const line = extractLineNumber(text);
    return Number.isFinite(line) ? score.total > line : true;
  }

  if (text.includes('alt')) {
    const line = extractLineNumber(text);
    return Number.isFinite(line) ? score.total < line : true;
  }

  return true;
};

const reconcileFullTimeScore = (card = {}, selection = '', scoreCandidates = []) => {
  const candidates = scoreCandidates
    .map((item) => (typeof item === 'string' ? item : item?.score))
    .filter(Boolean)
    .map(String);

  const compatible = candidates.find((score) => isScoreCompatibleWithSelection(score, selection));
  if (compatible) return compatible;
  return fallbackFullTimeScoreForSelection(selection, card) || candidates[0] || '-';
};

const fallbackHalfTimeScoreForFullTime = (fullTimeScore = '') => {
  const score = parseScore(fullTimeScore);
  if (!score) return '0-0';
  if (score.home > score.away && score.home >= 2) return '1-0';
  if (score.away > score.home && score.away >= 2) return '0-1';
  return '0-0';
};

const reconcileScoreList = (items = [], selection = '', card = {}) => {
  const seen = new Set();
  const compatible = (Array.isArray(items) ? items : [])
    .filter((item) => item?.score && isScoreCompatibleWithSelection(item.score, selection))
    .filter((item) => {
      if (seen.has(item.score)) return false;
      seen.add(item.score);
      return true;
    });

  if (compatible.length) return compatible;
  const fallback = fallbackFullTimeScoreForSelection(selection, card);
  return fallback && fallback !== '-' ? [{ score: fallback, probability: null }] : [];
};

const getProbabilityRows = (card = {}) => {
  const grid = card.probabilityGrid || {};
  return [
    ['MS 1', getValue(grid.homeWinProbability, card.homeWinProbability, card.ms1Probability)],
    ['MS X', getValue(grid.drawProbability, card.drawProbability, card.xProbability)],
    ['MS 2', getValue(grid.awayWinProbability, card.awayWinProbability, card.ms2Probability)],
    ['Üst 2.5', getValue(grid.over25Probability, card.over25Probability, card.ust25Probability)],
    ['Alt 2.5', getValue(grid.under25Probability, card.under25Probability, card.alt25Probability)],
    ['KG Var', getValue(grid.bttsYesProbability, card.bttsYesProbability, card.kgVarProbability)],
    ['KG Yok', getValue(grid.bttsNoProbability, card.bttsNoProbability, card.kgYokProbability)]
  ].filter(([, value]) => toNumber(value) !== null && Number(value) > 0);
};

const getMatchScenario = (card = {}) => {
  const scenario = card?.matchScenario || card?.scorePrediction?.matchScenario || card?.metrics?.matchScenario || card?.monteCarlo?.matchScenario || {};
  const tags = Array.isArray(card?.aiNarrativeTags) ? card.aiNarrativeTags : Array.isArray(scenario?.tags) ? scenario.tags : [];
  return {
    resultLean: scenario?.resultLean || tags[0] || '',
    tempo: scenario?.tempo || '',
    gameFlow: scenario?.gameFlow || '',
    pressure: scenario?.pressure || '',
    firstHalf: scenario?.firstHalf || '',
    goalTrend: scenario?.goalTrend || '',
    summary: scenario?.summary || '',
    tags: tags.filter(Boolean)
  };
};

const buildMonteCarloGameScenario = (card = {}, strongestScore = '') => {
  const fullScore = parseScore(strongestScore || card?.scorePrediction?.fullTime || card?.scorePrediction?.fullTimeScore || '');
  const halfScore = parseScore(card?.scorePrediction?.halfTime || card?.scorePrediction?.halfTimeScore || '');
  const expectedHome = toNumber(card?.monteCarlo?.expectedGoals?.home ?? card?.scorePrediction?.expectedGoals?.home ?? card?.teamMetrics?.home?.xg ?? card?.homeXg);
  const expectedAway = toNumber(card?.monteCarlo?.expectedGoals?.away ?? card?.scorePrediction?.expectedGoals?.away ?? card?.teamMetrics?.away?.xg ?? card?.awayXg);

  if (fullScore) {
    const diff = fullScore.home - fullScore.away;

    if (fullScore.home > 0 && fullScore.away > 0 && fullScore.total >= 3) {
      return 'Karşılıklı gol olası';
    }

    if (halfScore && halfScore.total === 0 && fullScore.total > 0) {
      return 'Geç gol senaryosu';
    }

    if (diff >= 1) {
      return 'Ev sahibi üstün';
    }

    if (Math.abs(diff) <= 1) {
      return 'Dengeli maç';
    }
  }

  if (expectedHome !== null && expectedAway !== null) {
    const expectedDiff = expectedHome - expectedAway;
    if (expectedHome > 0.8 && expectedAway > 0.8 && expectedHome + expectedAway >= 2.4) return 'Karşılıklı gol olası';
    if (expectedDiff >= 0.35) return 'Ev sahibi üstün';
  }

  return 'Dengeli maç';
};

const getMonteCarlo = (card = {}, selection = '') => {
  const mc = card?.monteCarlo || card?.scorePrediction?.monteCarlo || card?.metrics?.monteCarlo || {};
  const score = card?.scorePrediction || {};
  const mostLikelyFullTimeScores = reconcileScoreList(
    mc?.mostLikelyFullTimeScores || score?.mostLikelyFullTimeScores || score?.topScores || [],
    selection,
    card
  );
  const strongestScore = reconcileFullTimeScore(card, selection, [mc?.strongestScore, score?.mostLikelyFullTimeScores?.[0], score?.topFullTime?.score]);

  return {
    simulations: Number(mc?.simulations || score?.simulations || 10000),
    confidence: getValue(mc?.confidence, mc?.topOption?.probability),
    strongestScore,
    gameScenario: buildMonteCarloGameScenario(card, strongestScore),
    mostLikelyFullTimeScores,
    mostLikelyHalfTimeScores: mc?.mostLikelyHalfTimeScores || score?.mostLikelyHalfTimeScores || []
  };
};

const getXgPair = (card = {}, side = 'home') => {
  const teamMetrics = card.teamMetrics || card.metrics?.teamMetrics || {};
  const sideMetrics = teamMetrics?.[side] || {};
  const prefix = side === 'home' ? 'home' : 'away';

  return {
    xg: getValue(card?.[`${prefix}Xg`], card?.[`${prefix}xG`], card?.metrics?.[`${prefix}Xg`], sideMetrics?.xg, sideMetrics?.expectedGoals),
    xga: getValue(card?.[`${prefix}Xga`], card?.[`${prefix}XGA`], card?.metrics?.[`${prefix}Xga`], sideMetrics?.xga, sideMetrics?.expectedGoalsAgainst)
  };
};

const hasXgPairValue = (pair = {}) => Number(pair.xg || 0) > 0 || Number(pair.xga || 0) > 0;

const getExpectedGoals = (card = {}) => getValue(
  card?.expectedGoalsTotal,
  card?.metrics?.expectedGoals?.total,
  card?.totalExpectedGoals,
  card?.scorePrediction?.expectedGoals?.total,
  card?.monteCarlo?.expectedGoalsTotal
);

const formatSimulationCount = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return '10.000';
  return new Intl.NumberFormat('tr-TR').format(numeric);
};

const buildMatchProgressLabel = (card = {}, finalScore = '') => {
  if (finalScore) return `Final Skor: ${finalScore}`;

  const matchTime = parseMatchDate(getMatchDateValue(card));
  if (!matchTime) return 'Zaman bilgisi yok';

  const diffMinutes = (Date.now() - matchTime.getTime()) / 60000;
  if (diffMinutes < 0) return 'Maç başlamadı';
  if (diffMinutes <= 130) return 'Maç oynanıyor';
  return 'Maç tamamlandı - skor bekleniyor';
};

const styles = {
  card: {
    display: 'grid',
    gap: 14,
    width: '100%',
    minWidth: 0,
    padding: 18,
    borderRadius: 24,
    color: '#ffffff',
    boxShadow: '0 22px 58px rgba(0,0,0,.38)',
    overflow: 'hidden'
  },
  top: { display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center', flexWrap: 'wrap' },
  league: { maxWidth: '70%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', borderRadius: 999, padding: '8px 12px', background: 'rgba(24,76,123,.46)', color: '#dbeafe', fontSize: 12, fontWeight: 950 },
  status: { borderRadius: 999, padding: '8px 12px', background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.10)', fontSize: 12, fontWeight: 950 },
  date: { width: 'fit-content', borderRadius: 999, padding: '8px 10px', background: 'rgba(255,255,255,.055)', color: 'rgba(226,232,240,.80)', fontSize: 12, fontWeight: 850 },
  teams: { display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 78px minmax(0,1fr)', alignItems: 'center', gap: 12, minHeight: 86 },
  team: { minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 },
  teamHome: { justifyContent: 'flex-start', textAlign: 'left' },
  teamAway: { justifyContent: 'flex-end', textAlign: 'right' },
  teamName: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#fff6cc', fontSize: 17, fontWeight: 1000, lineHeight: 1.12 },
  logo: { width: 34, height: 34, flex: '0 0 34px', borderRadius: '50%', objectFit: 'contain', padding: 4, background: 'rgba(255,255,255,.08)', border: '1px solid rgba(212,175,55,.24)' },
  vsBox: { display: 'grid', placeItems: 'center', minHeight: 62, borderRadius: 18, background: 'rgba(255,255,255,.065)', border: '1px solid rgba(255,255,255,.10)', color: '#ffffff', fontSize: 26, fontWeight: 1000, lineHeight: 1 },
  vsSmall: { display: 'block', marginTop: 3, color: 'rgba(255,255,255,.55)', fontSize: 10, letterSpacing: '.14em', fontWeight: 900 },
  hero: { display: 'grid', gridTemplateColumns: 'minmax(0,1.4fr) minmax(92px,.55fr)', gap: 10, alignItems: 'stretch', padding: 14, borderRadius: 20, background: 'linear-gradient(145deg, rgba(0,0,0,.62), rgba(212,175,55,.08))', border: '1px solid rgba(212,175,55,.26)' },
  heroLabel: { display: 'block', color: '#f5d65f', fontSize: 12, fontWeight: 950, letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 7 },
  heroPick: { display: 'block', color: '#ffffff', fontSize: 30, lineHeight: 1.05, fontWeight: 1000, overflow: 'hidden', textOverflow: 'ellipsis' },
  confidenceBox: { display: 'grid', placeItems: 'center', textAlign: 'center', borderRadius: 18, background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.10)', padding: 10 },
  confidenceLabel: { display: 'block', color: '#f5d65f', fontSize: 11, fontWeight: 950, textTransform: 'uppercase', marginBottom: 4 },
  confidenceValue: { color: '#ffffff', fontSize: 26, lineHeight: 1, fontWeight: 1000 },
  coreGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 10 },
  coreItem: { minHeight: 84, display: 'grid', placeItems: 'center', gap: 5, textAlign: 'center', padding: '12px 10px', borderRadius: 18, background: 'rgba(255,255,255,.055)', border: '1px solid rgba(255,255,255,.075)' },
  coreLabel: { color: 'rgba(226,232,240,.72)', fontSize: 12, fontWeight: 900 },
  coreValue: { color: '#ffffff', fontSize: 22, fontWeight: 1000, lineHeight: 1.05 },
  button: { width: '100%', minHeight: 48, border: '1px solid rgba(212,175,55,.34)', borderRadius: 999, padding: '11px 14px', background: 'linear-gradient(145deg, rgba(35,28,9,.96), rgba(8,8,8,.98))', color: '#f6d35d', fontWeight: 950, cursor: 'pointer' },
  detailPanel: { display: 'grid', gap: 10, padding: 12, borderRadius: 18, background: 'rgba(255,255,255,.035)', border: '1px solid rgba(255,255,255,.065)' },
  sectionTitle: { margin: '4px 0 0', color: '#f5d65f', fontSize: 12, fontWeight: 1000, letterSpacing: '.08em', textTransform: 'uppercase' },
  miniGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 8 },
  metric: { minWidth: 0, borderRadius: 14, padding: '10px 11px', background: 'rgba(255,255,255,.045)', border: '1px solid rgba(255,255,255,.065)' },
  metricLabel: { display: 'block', color: 'rgba(226,232,240,.68)', fontSize: 11, fontWeight: 850, marginBottom: 4 },
  metricValue: { color: '#ffffff', fontSize: 15, fontWeight: 950 },
  scoreTags: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  tag: { borderRadius: 999, padding: '6px 9px', background: 'rgba(125,211,252,.10)', border: '1px solid rgba(125,211,252,.18)', color: '#dbeafe', fontSize: 11, fontWeight: 850 },
  monteCarlo: { borderRadius: 18, padding: 12, background: 'linear-gradient(135deg, rgba(38,88,63,.28), rgba(212,175,55,.08))', border: '1px solid rgba(63,255,141,.20)' },
  monteLine: { display: 'flex', justifyContent: 'space-between', gap: 10, padding: '4px 0', color: 'rgba(238,243,250,.84)', fontSize: 12, fontWeight: 800 },
  row: { display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 10, alignItems: 'center', padding: '9px 10px', borderRadius: 13, background: 'rgba(255,255,255,.048)', color: 'rgba(238,243,250,.78)', fontSize: 13 },
  left: { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  right: { color: '#ffffff', fontWeight: 900, textAlign: 'right' },
  summary: { margin: 0, color: 'rgba(226,232,240,.78)', fontSize: 12, lineHeight: 1.55 }
};

const AiAnalysisCard = ({ card }) => {
  const [openDetails, setOpenDetails] = useState(false);
  const probabilityRows = useMemo(() => getProbabilityRows(card || {}), [card]);

  if (!card) return null;

  const marketLabel = getAiRecommendation(card);
  const aiConfidence = getAiConfidence(card);
  const preliminaryFinalScore = String(getFinalScoreValue(card) || '').trim();
  const liveStatus = resolveFinishedStatusFromScore(card, marketLabel, preliminaryFinalScore);
  const statusMeta = getStatusMeta(liveStatus);
  const topStatusLabel = getTopStatusLabel(statusMeta, liveStatus, preliminaryFinalScore);
  const normalizedStatusForClass = String(liveStatus || 'pending').trim().toLowerCase();
  const homeName = card.homeTeamName || getName(card.homeTeam, getName(card.home, 'Ev sahibi'));
  const awayName = card.awayTeamName || getName(card.awayTeam, getName(card.away, 'Deplasman'));
  const homeLogo = getLogoUrl(card.homeTeam, card.home, card.homeLogo, card.homeTeamLogo, card.teams?.home?.logo);
  const awayLogo = getLogoUrl(card.awayTeam, card.away, card.awayLogo, card.awayTeamLogo, card.teams?.away?.logo);
  const leagueName = card.leagueName || getName(card.league, '-');
  const matchDateLabel = getMatchDateLabel(card);
  const scorePrediction = getScorePrediction(card, marketLabel);
  const scenario = getMatchScenario(card);
  const monteCarlo = getMonteCarlo(card, marketLabel);
  const expectedGoals = getExpectedGoals(card);
  const homeXgPair = getXgPair(card, 'home');
  const awayXgPair = getXgPair(card, 'away');
  const showXgMetrics = hasXgPairValue(homeXgPair) || hasXgPairValue(awayXgPair);
  const note = String(getValue(card.aiInsight, card.reason, card.analysis, card.evidence, scenario.summary, '') || '').trim();
  const matchProgressLabel = buildMatchProgressLabel(card, preliminaryFinalScore);
  const cardStyle = {
    ...styles.card,
    border: `1px solid ${statusMeta.border}`,
    background: statusMeta.bg
  };

  return (
    <article className={`ta-ai-card-fixed ta-ai-card-six-layout ta-ai-pro-card ${normalizedStatusForClass === 'won' ? 'ta-ai-won-card' : ''} ${normalizedStatusForClass === 'lost' ? 'ta-ai-lost-card' : ''} ${normalizedStatusForClass === 'live' ? 'ta-ai-live-card' : ''}`} style={cardStyle}>
      <div style={styles.top}>
        <span style={styles.league} title={leagueName}>{leagueName}</span>
        <span style={{ ...styles.status, color: statusMeta.text }}>{topStatusLabel}</span>
      </div>

      <div style={styles.date}>Maç tarihi: {matchDateLabel}</div>

      <div style={styles.teams}>
        <span style={{ ...styles.team, ...styles.teamHome }} title={homeName}>
          {homeLogo ? <img src={homeLogo} alt="" style={styles.logo} loading="lazy" /> : null}
          <span style={styles.teamName}>{homeName}</span>
        </span>
        <span style={styles.vsBox}>VS<span style={styles.vsSmall}>TAHMİN</span></span>
        <span style={{ ...styles.team, ...styles.teamAway }} title={awayName}>
          <span style={styles.teamName}>{awayName}</span>
          {awayLogo ? <img src={awayLogo} alt="" style={styles.logo} loading="lazy" /> : null}
        </span>
      </div>

      <div style={styles.hero}>
        <div>
          <span style={styles.heroLabel}>AI Ana Karar</span>
          <span style={styles.heroPick} title={marketLabel}>{marketLabel}</span>
        </div>
        <div style={styles.confidenceBox}>
          <span style={styles.confidenceLabel}>Güven</span>
          <strong style={styles.confidenceValue}>{formatPercent(aiConfidence)}</strong>
        </div>
      </div>

      <div style={styles.coreGrid}>
        <div style={styles.coreItem}>
          <span style={styles.coreLabel}>İlk Yarı</span>
          <strong style={styles.coreValue}>{scorePrediction.ht}</strong>
        </div>
        <div style={styles.coreItem}>
          <span style={styles.coreLabel}>Maç Sonu</span>
          <strong style={styles.coreValue}>{scorePrediction.ft}</strong>
        </div>
        <div style={styles.coreItem}>
          <span style={styles.coreLabel}>Beklenen Gol</span>
          <strong style={styles.coreValue}>{formatMetricNumber(expectedGoals)}</strong>
        </div>
      </div>

      <button type="button" style={styles.button} onClick={() => setOpenDetails((value) => !value)}>
        {openDetails ? 'Detayları Kapat' : 'Detaylı Analiz Aç'}
      </button>

      {openDetails ? (
        <div style={styles.detailPanel}>
          <div style={{ ...styles.row, background: statusMeta.chipBg }}>
            <span style={styles.left}>Durum</span>
            <span style={{ ...styles.right, color: statusMeta.text }}>{matchProgressLabel}</span>
          </div>

          <div style={styles.miniGrid}>
            <div style={styles.metric}>
              <span style={styles.metricLabel}>Maç Senaryosu</span>
              <strong style={styles.metricValue}>{scenario.gameFlow || 'Dengeli'}</strong>
            </div>
            <div style={styles.metric}>
              <span style={styles.metricLabel}>Baskı</span>
              <strong style={styles.metricValue}>{scenario.pressure || 'Dengeli'}</strong>
            </div>
            <div style={styles.metric}>
              <span style={styles.metricLabel}>İlk Yarı Eğilimi</span>
              <strong style={styles.metricValue}>{scenario.firstHalf || 'Dengeli'}</strong>
            </div>
            <div style={styles.metric}>
              <span style={styles.metricLabel}>Gol Eğilimi</span>
              <strong style={styles.metricValue}>{scenario.goalTrend || 'Dengeli'}</strong>
            </div>
          </div>

          <div style={styles.monteCarlo}>
            <div style={styles.sectionTitle}>Monte Carlo</div>
            <div style={styles.monteLine}><span>Simülasyon</span><strong>{formatSimulationCount(monteCarlo.simulations)} maç</strong></div>
            <div style={styles.monteLine}><span>En güçlü skor</span><strong>{monteCarlo.strongestScore || scorePrediction.ft}</strong></div>
            <div style={styles.monteLine}><span>Oyun senaryosu</span><strong>{monteCarlo.gameScenario}</strong></div>
            {monteCarlo.mostLikelyFullTimeScores?.slice(0, 4).length ? (
              <div style={styles.scoreTags}>
                {monteCarlo.mostLikelyFullTimeScores.slice(0, 4).map((item) => (
                  <span key={item.score} style={styles.tag}>
                    {item.score}{toNumber(item.probability) !== null ? ` ${formatPercent(item.probability)}` : ''}
                  </span>
                ))}
              </div>
            ) : null}
          </div>

          {probabilityRows.length ? (
            <>
              <div style={styles.sectionTitle}>Model Olasılıkları</div>
              <div style={styles.miniGrid}>
                {probabilityRows.map(([label, value]) => (
                  <div style={styles.metric} key={label}>
                    <span style={styles.metricLabel}>{label}</span>
                    <strong style={styles.metricValue}>{formatPercent(value)}</strong>
                  </div>
                ))}
              </div>
            </>
          ) : null}

          {showXgMetrics ? (
            <>
              <div style={styles.sectionTitle}>Takım Profili</div>
              <div style={styles.row}>
                <span style={styles.left}>Ev xG / xGA</span>
                <span style={styles.right}>{formatMetricNumber(homeXgPair.xg)} / {formatMetricNumber(homeXgPair.xga)}</span>
              </div>
              <div style={styles.row}>
                <span style={styles.left}>Dep xG / xGA</span>
                <span style={styles.right}>{formatMetricNumber(awayXgPair.xg)} / {formatMetricNumber(awayXgPair.xga)}</span>
              </div>
            </>
          ) : null}

          {scenario.tags?.length ? (
            <div style={styles.scoreTags}>
              {scenario.tags.slice(0, 6).map((tag) => <span key={tag} style={styles.tag}>{tag}</span>)}
            </div>
          ) : null}

          {note ? <p style={styles.summary}>{note}</p> : null}
        </div>
      ) : null}
    </article>
  );
};

if (typeof document !== 'undefined' && !document.getElementById('ta-ai-pro-card-layout-style')) {
  const style = document.createElement('style');
  style.id = 'ta-ai-pro-card-layout-style';
  style.innerHTML = `
    .ai-premium-layout-v2{width:min(1940px, calc(100vw - 20px))!important;max-width:1940px!important;}
    .ai-premium-layout-v2 .ai-analysis-grid{display:grid!important;grid-template-columns:repeat(3,minmax(0,1fr))!important;gap:16px!important;align-items:start!important;}
    .ta-ai-pro-card{transition:transform .18s ease,border-color .18s ease,box-shadow .18s ease;}
    .ta-ai-pro-card:hover{transform:translateY(-2px);box-shadow:0 26px 68px rgba(0,0,0,.46)!important;}
    .ta-ai-won-card{border-color:rgba(46,204,113,.58)!important;box-shadow:0 0 28px rgba(46,204,113,.10)!important;}
    .ta-ai-lost-card{border-color:rgba(255,80,80,.52)!important;box-shadow:0 0 28px rgba(231,76,60,.10)!important;}
    @media (max-width:1180px){.ai-premium-layout-v2 .ai-analysis-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important;}}
    @media (max-width:720px){.ai-premium-layout-v2 .ai-analysis-grid{grid-template-columns:1fr!important;}.ta-ai-pro-card{padding:15px!important;}.ta-ai-pro-card [style*="grid-template-columns: minmax(0px, 1fr) 78px"]{grid-template-columns:minmax(0,1fr) 66px minmax(0,1fr)!important;}}
  `;
  document.head.appendChild(style);
}

export default AiAnalysisCard;
